/**
 * MODULE: _shared/frontend-shell/src/design-system/layouts/TenantAdminLayout.tsx
 * PURPOSE: Layout component for tenant admin pages with sidebar, top bar, and tenant branding
 * 
 * DEPENDENCIES:
 *   - flowbite-react: Sidebar, TopBar components
 *   - react-router-dom: useLocation, useNavigate
 *   - @core/auth: useAuth, useCurrentUser
 *   - @core/tenant: useTenant
 *   - @design-system/components: Sidebar, TopBar, AiAssistantButton, AiAssistantPanel
 *   - @heroicons/react: HomeIcon, UsersIcon, Cog6ToothIcon, CreditCardIcon, QuestionMarkCircleIcon
 * 
 * PUBLIC API:
 *   - TenantAdminLayout: Main layout component
 *   - TenantAdminLayoutProps: Component props interface
 * 
 * SIDE EFFECTS:
 *   - React rendering
 *   - localStorage (sidebar state)
 *   - Navigation routing
 * 
 * AGENT SAFETY: 🟡 RESTRICTED - Layout component affecting routing
 * FILE SIZE: 212 / 300 lines
 * 
 * Agent Notes:
 * - Uses Flowbite design system components
 * - Includes sidebar navigation and top bar
 * - Tenant logo/branding slot in sidebar header
 * - Current plan badge
 * - Same responsive behavior as SuperAdminLayout
 * - 2026-04-10: Main scroll region uses `app-shell-disappearing-scroll` (root CHANGELOG [2.2.354]).
 * - 2026-04-26: Added AiAssistantButton (top of sidebar) + AiAssistantPanel (right slide-out) + selection toolbar; `main` uses **`id="main-content"`** for skip-link (App.tsx). **⌘⇧A / Ctrl⇧A** toggles the AI panel. Super admin parity: **`SuperAdminLayout`**.
 * - 2026-01-26: Initial Flowbite layout
 * 
 * See: /README.md | /ARCHITECTURE.md | /_shared/frontend-shell/README.md
 */

import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AiAssistantButton } from '../components/AiAssistant/AiAssistantButton';
import { AiAssistantPanel } from '../components/AiAssistant/AiAssistantPanel';
import { AssistantSelectionToolbar } from '../components/AiAssistant/AssistantSelectionToolbar';
import { subscribeAssistantOpenWithPrefill } from '../components/AiAssistant/assistantPrefillBridge';
import { Sidebar } from '../components/Navigation/Sidebar';
import { TopBar } from '../components/Navigation/TopBar';
import { useAuth, useCurrentUser } from '@core/auth';
import { useTenant } from '@core/tenant';
import {
  HomeIcon,
  UsersIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

interface TenantAdminLayoutProps {
  children: ReactNode;
  tenantLogo?: ReactNode;
  currentPlan?: string;
}

const TENANT_ADMIN_NAV_ITEMS = [
  {
    path: '/tenant-admin',
    label: 'Dashboard',
    icon: HomeIcon,
  },
  {
    path: '/tenant-admin/team',
    label: 'Team Members',
    icon: UsersIcon,
  },
  {
    path: '/tenant-admin/settings',
    label: 'Settings',
    icon: Cog6ToothIcon,
  },
  {
    path: '/tenant-admin/billing',
    label: 'Billing',
    icon: CreditCardIcon,
  },
  {
    path: '/tenant-admin/support',
    label: 'Support',
    icon: QuestionMarkCircleIcon,
  },
];

export function TenantAdminLayout({ 
  children, 
  tenantLogo,
  currentPlan = 'Pro',
}: TenantAdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user } = useCurrentUser();
  const { currentTenant } = useTenant();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [docTitle, setDocTitle] = useState(() =>
    typeof document !== 'undefined' ? document.title : ''
  );

  const aiPageContext = useMemo(
    () => ({
      path: `${location.pathname}${location.search}`,
      title: docTitle,
    }),
    [location.pathname, location.search, docTitle]
  );

  useEffect(() => {
    const id = requestAnimationFrame(() => setDocTitle(document.title));
    return () => cancelAnimationFrame(id);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        setAiPanelOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    return subscribeAssistantOpenWithPrefill(() => {
      setAiPanelOpen(true);
    });
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNotificationClick = () => {
    navigate('/tenant-admin/notifications');
  };

  // Use icon components directly (Flowbite expects component, not ReactNode)
  const navItems = TENANT_ADMIN_NAV_ITEMS.map((item) => ({
    path: item.path,
    label: item.label,
    icon: item.icon,
  }));

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <Sidebar
        userRole="tenant_admin"
        activePath={location.pathname}
        items={navItems}
        user={user ? {
          name: user.name,
          email: user.email,
          avatar_url: user.avatar_url,
        } : undefined}
        onLogout={handleLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={setSidebarCollapsed}
        customHeader={
          <div>
            <div className="mb-3">
              <AiAssistantButton
                collapsed={sidebarCollapsed}
                isOpen={aiPanelOpen}
                onClick={() => setAiPanelOpen((v) => !v)}
              />
            </div>
            {tenantLogo ? (
              <div>
                {tenantLogo}
                {currentPlan && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {currentPlan} Plan
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-1 flex items-center pl-2.5">
                <img src="/brazn-logo.png" alt="Brazn logo" className="mr-3 h-6 sm:h-7 rounded-md object-contain" />
                {!sidebarCollapsed && (
                  <span className="self-center whitespace-nowrap text-xl font-semibold dark:text-white">Brazn</span>
                )}
              </div>
            )}
          </div>
        }
      />

      {/* Main content area - responsive margin based on sidebar state */}
      <div 
        className="flex-1 flex flex-col overflow-hidden transition-all duration-300"
        style={{ 
          marginLeft: sidebarCollapsed ? '4rem' : '16rem',
        }}
      >
        {/* Top Bar */}
        <TopBar
          user={user ? {
            name: user.name,
            email: user.email,
            avatar_url: user.avatar_url,
          } : undefined}
          tenantName={currentTenant?.name}
          notifications={0} // Notification count: wire to centre when available
          onNotificationClick={handleNotificationClick}
          userMenuProfileTo="/tenant-admin/profile"
          userMenuWorkspaceTo="/tenant-admin/settings"
        />

        {/* Main content */}
        <main
          id="main-content"
          role="main"
          className="app-shell-disappearing-scroll flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900"
        >
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      <AiAssistantPanel
        isOpen={aiPanelOpen}
        pageContext={aiPageContext}
        onClose={() => setAiPanelOpen(false)}
      />
      <AssistantSelectionToolbar />
    </div>
  );
}
